/**
 * ConnectionDebugger Component
 *
 * Development-only component that logs wallet connection and authentication state
 * to help diagnose connection persistence issues.
 *
 * Only active in development mode (NODE_ENV === 'development')
 */

import { useEffect } from 'react';
import { useAppKitAccount, useAppKitState } from '@reown/appkit/react';

import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

function ConnectionDebugger() {
  const { address, isConnected } = useAppKitAccount();
  const { isAuthenticated, isAuthenticating, isVerifyingSession, userInfo } = useAuth();
  const appKitState = useAppKitState();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    // Log AppKit localStorage keys on mount
    const appKitKeys = Object.keys(localStorage).filter(
      (key) => key.includes('@w3m') || key.includes('@reown') || key.includes('wc@2')
    );

    logger.log('🔍 [ConnectionDebugger] AppKit localStorage keys:', appKitKeys);

    // Log initial connection state
    logger.log('🔍 [ConnectionDebugger] Initial state:', {
      wallet: {
        isConnected,
        address,
      },
      auth: {
        isAuthenticated,
        isAuthenticating,
        isVerifyingSession,
        userAddress: userInfo?.address,
      },
      appKit: {
        open: appKitState.open,
        selectedNetworkId: appKitState.selectedNetworkId,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Log state changes
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    logger.log('🔄 [ConnectionDebugger] State changed:', {
      wallet: {
        isConnected,
        address,
      },
      auth: {
        isAuthenticated,
        isAuthenticating,
        isVerifyingSession,
        userAddress: userInfo?.address,
      },
    });
  }, [isConnected, address, isAuthenticated, isAuthenticating, isVerifyingSession, userInfo]);

  // No UI - debug only
  return null;
}

export default ConnectionDebugger;
